import React, { useState, useContext, useMemo, createContext, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AppTable, AppButton, AppTooltip, AppTableActionButton } from '@/components/atoms';
import type { ColumnsType } from 'antd/es/table';
import type { DragEndEvent } from '@dnd-kit/core';
import useAppModal from '@/hooks/useAppModal';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useMainFreeMedicalCategories,
  useMainFreeMedicalCategoryReorder,
} from '@/hooks/main/useMainFreeMedical';
import { useFreeMedicalCategoryModal } from '@/hooks/main/useFreeMedicalModal';
import { FreeMedicalTreatmentCategory, PaginationInfo, FreeMedicalInitData } from '../types';
import { FreeMedicalCategoryEditModal } from '@/components/organisms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { getAntdTableSortOrder } from '@/utils';

interface CategoryTabProps {
  initData: FreeMedicalInitData | null;
}

/** Listeners from useSortable for drag handle (avoids internal @dnd-kit path). */
type DragHandleListeners = Record<string, unknown>;

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: DragHandleListeners;
}

const RowContext = createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <AppButton
      type="text"
      size="small"
      icon={<i className="fa fa-bars" aria-hidden="true"></i>}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row: React.FC<RowProps> = props => {
  // ensure id is a string so it matches SortableContext items
  const sortableId = String(props['data-row-key']);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'text', // ensure text cursor except on drag handle/actions
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const CategoryTab: React.FC<CategoryTabProps> = ({ initData }) => {
  const queryClient = useQueryClient();
  // local sort state (single page, 100 items)
  const [categorySortField, setCategorySortField] = useState<string>('sort_number');
  const [categorySortOrder, setCategorySortOrder] = useState<string>('asc');
  const [dataSource, setDataSource] = useState<FreeMedicalTreatmentCategory[]>([]);

  // modal state & handlers
  const {
    categoryEditModalOpen,
    setCategoryEditModalOpen,
    selectedCategory,
    isNewCategory,
    handleNewCategory,
    handleEditCategory,
    deleteCategoryMutation,
    handleSaveCategory,
  } = useFreeMedicalCategoryModal();

  const { confirm } = useAppModal();

  const handleDeleteCategory = useCallback(
    (category: FreeMedicalTreatmentCategory) => {
      confirm({
        title: 'カテゴリ削除確認',
        styles: {
          body: {
            maxWidth: '100%',
            minWidth: 0,
            wordBreak: 'break-all',
            overflowWrap: 'break-word',
          },
        },
        content: (
          <div
            style={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              wordBreak: 'break-all',
              overflowWrap: 'break-word',
              paddingRight: 8,
            }}
          >
            {`カテゴリ「${category.category_name}」を削除しますか？`}
          </div>
        ),
        okText: '削除',
        cancelText: 'キャンセル',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await deleteCategoryMutation.mutateAsync(category.id);
          } catch {
            // onError handled by mutation hook
          }
        },
      });
    },
    [confirm, deleteCategoryMutation]
  );

  // reorder hook
  const { mutateAsync: reorderCategories, isLoading: isReorderingApi } =
    useMainFreeMedicalCategoryReorder();

  // query for categories (single page, 100 items)
  const { data: categoriesResponse, isLoading: categoriesLoading } = useMainFreeMedicalCategories({
    per_page: 100,
    page: 1,
    sort_field: categorySortField,
    sort_order: categorySortOrder,
  });

  const categories: FreeMedicalTreatmentCategory[] = categoriesResponse?.data?.data || [];
  const pagination: PaginationInfo = categoriesResponse?.data?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 100,
    total: 0,
  };

  // Keep local dataSource in sync with server results but avoid infinite loops
  useEffect(() => {
    if (!categoriesLoading && categories !== dataSource) {
      setDataSource(categories);
    }
  }, [categoriesLoading, categories, dataSource]);

  // Handle drag end
  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (active.id === over?.id || !over) {
      return;
    }

    const activeIndex = dataSource.findIndex(record => String(record.id) === String(active.id));
    const overIndex = dataSource.findIndex(record => String(record.id) === String(over.id));

    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    // Create new sorted array
    const newDataSource = arrayMove(dataSource, activeIndex, overIndex);

    setDataSource(newDataSource);

    // Build sort order map
    const sortOrders: Record<number, number> = {};
    newDataSource.forEach((category: FreeMedicalTreatmentCategory, index: number) => {
      sortOrders[category.id] = index + 1;
    });

    try {
      await reorderCategories(sortOrders);
    } catch (error) {
      // Revert on error
      setDataSource(categories);
    }
  };

  const columns: ColumnsType<FreeMedicalTreatmentCategory> = [
    {
      title: '',
      key: 'sort',
      className: 'w-10',
      align: 'center',
      render: () => <DragHandle />,
    },
    {
      title: 'カテゴリ名',
      dataIndex: 'category_name',
      key: 'category_name',
      align: 'center',
      sorter: true,
      sortOrder: getAntdTableSortOrder(categorySortField, 'category_name', categorySortOrder),
      className: '',
      width: '70%',
      render: (text: string) => (
        <AppTooltip title={text} showOnlyOnOverflow>
          <div className="max-w-full min-w-0 overflow-hidden text-left break-all line-clamp-1">
            {text}
          </div>
        </AppTooltip>
      ),
    },
    {
      title: '区分',
      dataIndex: 'product_sale_type',
      key: 'product_sale_type',
      align: 'center',
      render: (_: any, record: FreeMedicalTreatmentCategory) =>
        record.product_sale_type?.type_name ?? '-',
      sorter: true,
      sortOrder: getAntdTableSortOrder(categorySortField, 'product_sale_type', categorySortOrder),
    },
    {
      title: '操作',
      key: 'actions',
      align: 'center',
      width: 170,
      render: (_: any, record: FreeMedicalTreatmentCategory) => (
        <div className="flex justify-center gap-1">
          <AppTableActionButton action="edit" onClick={() => handleEditCategory(record)}>
            編集
          </AppTableActionButton>
          <AppTableActionButton action="delete" onClick={() => handleDeleteCategory(record)}>
            削除
          </AppTableActionButton>
        </div>
      ),
    },
  ];

  return (
    <CollapsibleCard title="カテゴリ">
      <div className="flex items-center justify-end mb-4">
        <AppButton onClick={handleNewCategory} className="flex items-center gap-2" type="primary">
          <i className="fa fa-plus" />
          新規登録
        </AppButton>
      </div>
      <div className="overflow-x-auto">
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={dataSource.map(i => String(i.id))}
            strategy={verticalListSortingStrategy}
          >
            <AppTable
              className="w-full text-sm border-collapse"
              scroll={{}} // or scroll={false}
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              rowKey="id"
              recordCount={pagination.total}
              dragTextName="カテゴリ一覧"
              loading={categoriesLoading || isReorderingApi}
              components={{ body: { row: Row } }}
              onChange={(_pagination, _filters, sorter) => {
                if (isReorderingApi) return;
                const sort = Array.isArray(sorter) ? sorter[0] : sorter;

                // If clicking to clear sort (third click), toggle to opposite direction instead
                if (!sort || !sort.field || !sort.order) {
                  // Toggle the current sort order instead of clearing
                  const newOrder = categorySortOrder === 'asc' ? 'desc' : 'asc';
                  setCategorySortOrder(newOrder);
                  return;
                }

                const sortOrder = sort.order === 'descend' ? 'desc' : 'asc';
                setCategorySortField(sort.field as string);
                setCategorySortOrder(sortOrder);
              }}
            />
          </SortableContext>
        </DndContext>
      </div>

      <FreeMedicalCategoryEditModal
        isOpen={categoryEditModalOpen}
        category={selectedCategory}
        isNew={isNewCategory}
        initData={initData}
        onClose={() => {
          setCategoryEditModalOpen(false);
          queryClient.invalidateQueries({
            predicate: query =>
              query.queryKey[0] === 'free-medical' &&
              query.queryKey[1] === 'categories' &&
              !query.queryKey.includes('detail'),
          });
        }}
        onSave={handleSaveCategory}
      />
    </CollapsibleCard>
  );
};

export default React.memo(CategoryTab);
